/**
 * TODO
 * WIP
 */
import React, { PropsWithChildren, useId } from 'react';

import {
  FieldValues,
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  UseFormReturn,
} from 'react-hook-form';

import { AtomProps } from '@/uiKit/atoms/utils/AtomTypes.ts';

export type BaseFormProps<T extends FieldValues> = PropsWithChildren<{
  onSubmit: SubmitHandler<T>;
  onError?: SubmitErrorHandler<T>;
  formContext: UseFormReturn<T>;
}>;

export type FormProps<T extends FieldValues> = AtomProps<BaseFormProps<T>>;

/**
 * A generic Form component for managing form submission and validation.
 * It provides integration with form context and utility methods for handling
 * submission and error states, along with customizable properties for flexibility.
 *
 * @template T Extends `FieldValues`, representing the structure of the form's fields.
 * @param {FormProps<T>} props - The properties for configuring the Form component.
 * @param {string} [props.id] - An optional unique identifier for the form. If not provided, a generated ID will be used.
 * @param {string} [props.className] - An optional CSS class for styling the form.
 * @param {function} props.onSubmit - A callback function to handle the form's submit event, typically used to process valid input data.
 * @param {function} [props.onError] - An optional callback function invoked when form validation fails.
 * @param {React.ReactNode} props.children - The child components or elements to be rendered inside the form.
 * @param {Object} props.formContext - The context object used for managing the form's state and validation logic.
 * @returns {React.ReactElement} The rendered Form component.
 */
export const Form = <T extends FieldValues>({
  id,
  className,
  onSubmit,
  onError,
  children,
  formContext,
  ...props
}: FormProps<T>): React.ReactElement => {
  const alternativeId = useId();
  id = id ?? alternativeId;

  return (
    <FormProvider {...formContext}>
      <form
        id={id}
        className={className}
        target="_blank"
        onSubmit={event => formContext.handleSubmit(onSubmit, onError)(event)}
        {...props}
      >
        {children}
      </form>
    </FormProvider>
  );
};
